import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import CloseIcon from '@mui/icons-material/Close'
import SendOutlinedIcon from '@mui/icons-material/SendOutlined'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Typography,
} from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { ReactNode, useEffect, useState } from 'react'
import ChannelCompose from './ChannelCompose'
import ConfirmSendDialog from './ConfirmSendDialog'
import RecipientRow, { SendConsumer, SendTargets } from './RecipientRow'
import MessageTemplateSelect from './MessageTemplateSelect'
import { useComposeChecks } from './composeChecks'
import { applyTemplate } from './messageTemplates'
import { buildContent, ChannelDraft, ChannelDrafts } from './sendContent'
import { toGatewayError } from '@/utils/gatewayError'
import { newUuid } from '@/utils/uuid'
import {
  getNotificationChannels,
  getConsumerTargets,
  sendNotifications,
  SendAudience,
  SendCounts,
  SendRequest,
} from '@/services/notificationService'

type Props = {
  open: boolean
  onClose: () => void
  accountId: number
  audience: SendAudience
  // set when the audience is one known consumer: shows the name and targets
  consumer?: SendConsumer
  // a warning the caller shows on the compose and the confirm step; it never blocks the send
  audienceWarning?: ReactNode
}

// A Gateway 400 or 409 says what is wrong (no sender for a channel, a
// disallowed variable). Other statuses carry nothing a user can act on.
const withGatewayDetail = (copy: string, error: unknown): string => {
  const { statusCode, message } = toGatewayError(error, '')
  return (statusCode === 400 || statusCode === 409) && message
    ? `${copy} (${message})`
    : copy
}

const SendNotificationDialog = ({
  open,
  onClose,
  accountId,
  audience,
  consumer,
  audienceWarning,
}: Props) => {
  const [tickedIds, setTickedIds] = useState<number[]>([])
  const [drafts, setDrafts] = useState<ChannelDrafts>({})
  const [counts, setCounts] = useState<SendCounts | null>(null)
  const [idempotencyKey, setIdempotencyKey] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sentCount, setSentCount] = useState<number | null>(null)

  // full reset on every open — a reopened dialog must never reuse a minted
  // key, show counts from a previous audience or keep the previous texts
  useEffect(() => {
    if (open) {
      setTickedIds([])
      setDrafts({})
      setCounts(null)
      setIdempotencyKey(null)
      setError(null)
    }
  }, [open])

  const { data: channels = [], isFetched: channelsFetched } = useQuery({
    queryKey: ['notification-channels', accountId],
    queryFn: () => getNotificationChannels(accountId),
    enabled: open,
  })
  // the targets are those of one premise, so a wider audience loads none
  const targetPremiseId =
    consumer && audience.kind === 'premises' && audience.ids.length === 1
      ? audience.ids[0]
      : null
  const targetsQuery = useQuery({
    queryKey: ['consumer-targets', accountId, targetPremiseId],
    queryFn: () => getConsumerTargets(accountId, targetPremiseId as number),
    enabled: open && targetPremiseId !== null,
  })
  const targets: SendTargets = {
    status: targetsQuery.status,
    phones: targetsQuery.data?.phones ?? [],
    emails: targetsQuery.data?.emails ?? [],
  }
  const content = buildContent(channels, tickedIds, drafts)
  const checks = useComposeChecks(accountId, open, channels, tickedIds, drafts)
  const canSend =
    !busy &&
    content.channelIds.length > 0 &&
    checks.everyTickedHasText &&
    !checks.overLimit &&
    checks.unknown.length === 0 &&
    checks.malformed.length === 0

  const request = (dryRun: boolean, key?: string): SendRequest => ({
    audience,
    ...content,
    dryRun,
    ...(key ? { idempotencyKey: key } : {}),
  })

  const dryRun = async () => {
    setBusy(true)
    setError(null)
    try {
      setCounts(await sendNotifications(accountId, request(true)))
      setIdempotencyKey(newUuid())
    } catch (e) {
      // Dry-run failure is a recipient-check failure, not a send failure —
      // distinct copy so the user knows nothing was (almost) sent (spec §C10).
      setError(withGatewayDetail('בדיקת הנמענים נכשלה — נסו שוב', e))
    } finally {
      setBusy(false)
    }
  }

  const cancelConfirm = () => {
    setCounts(null)
    setIdempotencyKey(null)
    setError(null)
  }

  const send = async () => {
    if (!idempotencyKey) return
    setBusy(true)
    setError(null)
    try {
      const res = await sendNotifications(
        accountId,
        request(false, idempotencyKey)
      )
      setSentCount(res.notificationsEnqueued)
      onClose()
    } catch (e) {
      setError(withGatewayDetail('השליחה נכשלה — נסו שוב', e))
    } finally {
      setBusy(false)
    }
  }

  const setDraft = (id: number, next: ChannelDraft) =>
    setDrafts(prev => ({ ...prev, [id]: next }))
  const setTicked = (id: number, ticked: boolean) =>
    setTickedIds(prev =>
      ticked ? [...prev.filter(x => x !== id), id] : prev.filter(x => x !== id)
    )

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="lg"
        aria-labelledby="send-dialog-title"
      >
        <DialogTitle
          id="send-dialog-title"
          sx={{
            direction: 'rtl',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            fontSize: 16,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <ChatBubbleOutlineIcon fontSize="small" color="success" />
          <Box sx={{ flexGrow: 1 }}>כתיבת הודעה חדשה</Box>
          <IconButton aria-label="סגירה" size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ direction: 'rtl', pt: '24px !important' }}>
          {audienceWarning && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {audienceWarning}
            </Alert>
          )}
          {error && counts === null && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <RecipientRow
            consumer={consumer}
            targets={targets}
            templateSelect={
              <MessageTemplateSelect
                onPick={template =>
                  setDrafts(prev =>
                    applyTemplate(template, channels, tickedIds, prev)
                  )
                }
              />
            }
          />
          {channelsFetched && channels.length === 0 && (
            <Typography variant="caption">אין ערוצי שליחה פעילים</Typography>
          )}
          <ChannelCompose
            accountId={accountId}
            channels={channels}
            tickedIds={tickedIds}
            onTick={setTicked}
            drafts={drafts}
            onDraft={setDraft}
            checks={checks}
            customerName={consumer?.name}
          />
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'flex-end', px: 3, pb: 2 }}>
          <Button
            variant="contained"
            onClick={dryRun}
            disabled={!canSend}
            endIcon={<SendOutlinedIcon sx={{ transform: 'scaleX(-1)' }} />}
          >
            שלח
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmSendDialog
        counts={open ? counts : null}
        tableAudience={audience.kind === 'area' || audience.kind === 'search'}
        warning={audienceWarning}
        busy={busy}
        error={error}
        onConfirm={send}
        onCancel={cancelConfirm}
      />
      <Snackbar
        open={sentCount !== null}
        autoHideDuration={5000}
        onClose={() => setSentCount(null)}
        message={sentCount !== null ? `${sentCount} הודעות נשלחו` : ''}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        ContentProps={{ dir: 'rtl' }}
      />
    </>
  )
}

export default SendNotificationDialog
